import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ChevronRight,
  ChevronDown,
  MapPin,
  ExternalLink,
  Check,
  Package,
  Globe,
  Truck,
  ClipboardCheck,
  Ship,
  Shield,
  Users,
  Award,
  FileCheck,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { COMPANY_STATS } from "../constants/stats";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Country list for dropdown
const countries = [
  "Afghanistan", "Bangladesh", "China", "Egypt", "Ethiopia", "Ghana", "Hong Kong",
  "India", "Indonesia", "Japan", "Kenya", "Malaysia", "Nigeria", "Pakistan",
  "Philippines", "Saudi Arabia", "South Africa", "South Korea", "Sri Lanka",
  "Tanzania", "Thailand", "UAE", "Uganda", "United Kingdom", "USA", "Vietnam"
];

const volumeOptions = [
  "900 — 2,000 panels",
  "2,000 — 5,000 panels",
  "5,000 — 10,000 panels",
  "10,000+ panels",
];

const FeatureCard = ({ icon: Icon, title, description }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
    className="bg-navy-900 border border-slate-700 p-6 md:p-8"
  >
    <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4">
      <Icon className="text-gold" size={24} strokeWidth={1.5} />
    </div>
    <h3 className="font-outfit text-lg font-semibold text-white mb-3">{title}</h3>
    <p className="text-slate-400 text-sm leading-relaxed">{description}</p>
  </motion.div>
);

const ProcessStep = ({ number, title, description }) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0 w-10 h-10 bg-gold text-navy font-outfit font-bold flex items-center justify-center">
      {number}
    </div>
    <div>
      <h4 className="font-outfit font-semibold text-white mb-1">{title}</h4>
      <p className="text-slate-400 text-sm leading-relaxed">{description}</p>
    </div>
  </div>
);

const BuyPanelsPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    country: "",
    phone: "",
    email: "",
    volume_required: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    document.title = t("buyPanels.meta.title");
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", t("buyPanels.meta.description"));
    }
  }, [t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/enquiries/international`, formData);
      toast.success(t("buyPanels.bulk.form.toastSuccess"));
      setFormData({
        name: "",
        company: "",
        country: "",
        phone: "",
        email: "",
        volume_required: "",
        message: "",
      });
    } catch (error) {
      toast.error(t("buyPanels.bulk.form.toastError"));
      console.error("Form submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div data-testid="buy-panels-page" className="bg-navy">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        {/* Gold dot grid pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%">
            <pattern id="dot-pattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1.5" fill="#C5A059" />
            </pattern>
            <rect fill="url(#dot-pattern)" width="100%" height="100%" />
          </svg>
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="font-outfit text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6">
              {t("buyPanels.hero.title")}<br />
              <span className="text-gold">{t("buyPanels.hero.titleAccent")}</span>
            </h1>
            <p className="text-slate-300 text-lg md:text-xl max-w-3xl mx-auto mb-10 leading-relaxed">
              {t("buyPanels.hero.subtitle")}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => scrollToSection("retail")}
                data-testid="hero-retail-btn"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gold text-navy px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-gold-light transition-colors"
              >
                {t("buyPanels.hero.retailButton")}
                <ChevronDown size={16} />
              </button>
              <button
                onClick={() => scrollToSection("bulk")}
                data-testid="hero-bulk-btn"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border-2 border-gold text-gold px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-gold hover:text-navy transition-all"
              >
                {t("buyPanels.hero.bulkButton")}
                <ChevronDown size={16} />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION A — RETAIL BUYERS */}
      <section id="retail" className="py-20 md:py-32 bg-slate-900 scroll-mt-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <span className="inline-block px-4 py-2 bg-gold/20 text-gold text-xs font-bold uppercase tracking-wider mb-4">
              {t("buyPanels.retail.badge")}
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white">
              {t("buyPanels.retail.title")}
            </h2>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left column - Content */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="font-outfit text-xl font-semibold text-white mb-6">
                {t("buyPanels.retail.stockTitle")}
              </h3>
              <ul className="space-y-4 mb-10">
                {[
                  t("buyPanels.retail.stockItem1"),
                  t("buyPanels.retail.stockItem2"),
                  t("buyPanels.retail.stockItem3"),
                  t("buyPanels.retail.stockItem4"),
                  t("buyPanels.retail.stockItem5"),
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-slate-300">
                    <Check className="text-gold mt-1 flex-shrink-0" size={18} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              {/* UK Location */}
              <div className="bg-navy border border-slate-700 p-6 mb-6">
                <div className="flex items-start gap-3 mb-3">
                  <MapPin className="text-gold flex-shrink-0" size={20} />
                  <div>
                    <h4 className="font-outfit font-semibold text-white">{t("buyPanels.retail.ukLocationTitle")}</h4>
                    <p className="text-slate-400 text-sm">{t("buyPanels.retail.ukLocationArea")}</p>
                  </div>
                </div>
                <address className="not-italic text-slate-300 ms-8 mb-4">
                  Avondale Street, Oldham, OL8 1QD
                </address>
                <a
                  href="https://www.google.com/maps/search/Avondale+Street+Oldham+OL8+1QD"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-gold text-sm font-medium hover:underline ms-8"
                >
                  {t("buyPanels.retail.mapLink")}
                  <ExternalLink size={14} />
                </a>
              </div>

              {/* South Korea */}
              <div className="bg-navy border border-slate-700 p-6">
                <div className="flex items-start gap-3">
                  <Globe className="text-gold flex-shrink-0" size={20} />
                  <div>
                    <h4 className="font-outfit font-semibold text-white">{t("buyPanels.retail.koreaTitle")}</h4>
                    <p className="text-slate-400 text-sm">
                      {t("buyPanels.retail.koreaDescription")}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right column - Marketplace buttons */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              {/* eBay Button */}
              <a
                href="https://ebay.us/m/XZWRVV"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ebay-link"
                className="block bg-white p-8 border-2 border-transparent hover:border-gold transition-all group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#E53238] rounded flex items-center justify-center">
                      <span className="text-white font-bold text-lg">e</span>
                    </div>
                    <span className="text-[#E53238] font-bold text-xl">{t("buyPanels.retail.ebayLabel")}</span>
                  </div>
                  <ExternalLink className="text-slate-400 group-hover:text-gold transition-colors" size={20} />
                </div>
                <h3 className="font-outfit text-xl font-semibold text-navy mb-2">
                  {t("buyPanels.retail.ebayHeading")}
                </h3>
                <p className="text-slate-600">{t("buyPanels.retail.ebaySubtext")}</p>
              </a>

              {/* Facebook Button */}
              <a
                href="https://www.facebook.com/marketplace/profile/61577288068819/?ref=permalink"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="facebook-link"
                className="block bg-white p-8 border-2 border-transparent hover:border-gold transition-all group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#1877F2] rounded flex items-center justify-center">
                      <span className="text-white font-bold text-lg">f</span>
                    </div>
                    <span className="text-[#1877F2] font-bold text-xl">{t("buyPanels.retail.facebookLabel")}</span>
                  </div>
                  <ExternalLink className="text-slate-400 group-hover:text-gold transition-colors" size={20} />
                </div>
                <h3 className="font-outfit text-xl font-semibold text-navy mb-2">
                  {t("buyPanels.retail.facebookHeading")}
                </h3>
                <p className="text-slate-600">{t("buyPanels.retail.facebookSubtext")}</p>
              </a>

              {/* Note */}
              <div className="bg-gold/10 border border-gold/30 p-6">
                <p className="text-slate-300 text-sm leading-relaxed">
                  {t("buyPanels.retail.note")}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Visual Divider */}
      <div className="py-12 bg-navy text-center">
        <p className="text-slate-400 text-lg mb-4">{t("buyPanels.retail.dividerText")}</p>
        <button
          onClick={() => scrollToSection("bulk")}
          className="inline-flex items-center justify-center text-gold hover:text-gold-light transition-colors"
        >
          <ChevronDown size={32} className="animate-bounce" />
        </button>
      </div>

      {/* SECTION B — BULK BUYERS */}
      <section id="bulk" className="py-20 md:py-32 bg-navy scroll-mt-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <span className="inline-block px-4 py-2 bg-gold/20 text-gold text-xs font-bold uppercase tracking-wider mb-4">
              {t("buyPanels.bulk.badge")}
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white">
              {t("buyPanels.bulk.title")}
            </h2>
          </motion.div>

          {/* Feature Cards */}
          <div className="grid sm:grid-cols-2 gap-6 mb-16">
            <FeatureCard
              icon={ClipboardCheck}
              title={t("buyPanels.bulk.feature1.title")}
              description={t("buyPanels.bulk.feature1.description")}
            />
            <FeatureCard
              icon={Globe}
              title={t("buyPanels.bulk.feature2.title")}
              description={t("buyPanels.bulk.feature2.description")}
            />
            <FeatureCard
              icon={Package}
              title={t("buyPanels.bulk.feature3.title")}
              description={t("buyPanels.bulk.feature3.description")}
            />
            <FeatureCard
              icon={Truck}
              title={t("buyPanels.bulk.feature4.title")}
              description={t("buyPanels.bulk.feature4.description")}
            />
          </div>

          {/* What We Supply */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 mb-16">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="font-outfit text-xl font-semibold text-white mb-6">
                {t("buyPanels.bulk.supplyTitle")}
              </h3>
              <ul className="space-y-3">
                {[
                  t("buyPanels.bulk.supplyItem1"),
                  t("buyPanels.bulk.supplyItem2"),
                  t("buyPanels.bulk.supplyItem3"),
                  t("buyPanels.bulk.supplyItem4"),
                  t("buyPanels.bulk.supplyItem5"),
                  t("buyPanels.bulk.supplyItem6"),
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-slate-300">
                    <span className="w-1.5 h-1.5 bg-gold rounded-full mt-2 flex-shrink-0"></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* How It Works */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="font-outfit text-xl font-semibold text-white mb-6">
                {t("buyPanels.bulk.processTitle")}
              </h3>
              <div className="space-y-6">
                <ProcessStep
                  number={1}
                  title={t("buyPanels.bulk.step1.title")}
                  description={t("buyPanels.bulk.step1.description")}
                />
                <ProcessStep
                  number={2}
                  title={t("buyPanels.bulk.step2.title")}
                  description={t("buyPanels.bulk.step2.description")}
                />
                <ProcessStep
                  number={3}
                  title={t("buyPanels.bulk.step3.title")}
                  description={t("buyPanels.bulk.step3.description")}
                />
                <ProcessStep
                  number={4}
                  title={t("buyPanels.bulk.step4.title")}
                  description={t("buyPanels.bulk.step4.description")}
                />
                <ProcessStep
                  number={5}
                  title={t("buyPanels.bulk.step5.title")}
                  description={t("buyPanels.bulk.step5.description")}
                />
              </div>
            </motion.div>
          </div>

          {/* Bulk Enquiry Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <div className="bg-slate-900 border border-slate-700 p-8 md:p-12">
              <h3 className="font-outfit text-2xl font-semibold text-white mb-2 text-center">
                {t("buyPanels.bulk.form.title")}
              </h3>
              <p className="text-slate-400 text-center mb-8">
                {t("buyPanels.bulk.form.subtitle")}
              </p>

              <form onSubmit={handleSubmit} data-testid="bulk-enquiry-form">
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <input
                    type="text"
                    name="name"
                    placeholder={t("buyPanels.bulk.form.namePlaceholder")}
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-navy border border-slate-600 text-white placeholder-slate-500 form-input"
                    data-testid="bulk-form-name"
                  />
                  <input
                    type="text"
                    name="company"
                    placeholder={t("buyPanels.bulk.form.companyPlaceholder")}
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-navy border border-slate-600 text-white placeholder-slate-500 form-input"
                    data-testid="bulk-form-company"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-navy border border-slate-600 text-white form-input"
                    data-testid="bulk-form-country"
                  >
                    <option value="">{t("buyPanels.bulk.form.countryPlaceholder")}</option>
                    {countries.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    name="phone"
                    placeholder={t("buyPanels.bulk.form.phonePlaceholder")}
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-navy border border-slate-600 text-white placeholder-slate-500 form-input"
                    data-testid="bulk-form-phone"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <input
                    type="email"
                    name="email"
                    placeholder={t("buyPanels.bulk.form.emailPlaceholder")}
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-navy border border-slate-600 text-white placeholder-slate-500 form-input"
                    data-testid="bulk-form-email"
                  />
                  <select
                    name="volume_required"
                    value={formData.volume_required}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-navy border border-slate-600 text-white form-input"
                    data-testid="bulk-form-volume"
                  >
                    <option value="">{t("buyPanels.bulk.form.volumePlaceholder")}</option>
                    {volumeOptions.map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </div>
                <textarea
                  name="message"
                  placeholder={t("buyPanels.bulk.form.messagePlaceholder")}
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 bg-navy border border-slate-600 text-white placeholder-slate-500 form-input mb-6 resize-none"
                  data-testid="bulk-form-message"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="bulk-form-submit"
                  className="w-full bg-gold text-navy px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-gold-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? t("buyPanels.bulk.form.submittingButton") : t("buyPanels.bulk.form.submitButton")}
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-12 bg-slate-900 border-t border-slate-800">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
            {[
              { icon: Award, text: t("buyPanels.trustBar.yearsExperience", { years: COMPANY_STATS.yearsExperience }) },
              { icon: Ship, text: t("buyPanels.trustBar.containersShipped", { containers: COMPANY_STATS.containers }) },
              { icon: Users, text: t("buyPanels.trustBar.worldwideClients", { clients: COMPANY_STATS.clients }) },
              { icon: FileCheck, text: t("buyPanels.trustBar.wasteCarrier") },
              { icon: Shield, text: t("buyPanels.trustBar.inspectedStock") },
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3 text-slate-400">
                <item.icon size={20} className="text-gold" />
                <span className="text-sm font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BuyPanelsPage;
